import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import ReverifyPanel from "@/components/ReverifyPanel";
import SortHeader, { sortRows } from "@/components/SortHeader";
import { Warning, ArrowSquareOut, ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

/**
 * OCR Re-check — separate admin page (v1.20.1 + v1.20.9 sortable UTR).
 * - Full ReverifyPanel (party-wise controls) at the top
 * - Below: table of currently-flagged slips only. Rows self-remove
 *   the moment the flag clears (admin edits + saves the correction).
 * - "Open slip" jumps into the Uploads drawer with the slip pre-opened.
 */
export default function OcrRecheck() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ by: null, dir: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/uploads/flagged");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);
    // Poll every 10s so resolved rows self-remove even without a manual refresh.
    useEffect(() => {
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, [load]);

    const filtered = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = !q ? rows : rows.filter((r) =>
            (r.party_name || "").toLowerCase().includes(q) ||
            (r.utr || "").toLowerCase().includes(q)
        );
        return sortRows(base, sort);
    }, [rows, search, sort]);

    const openSlip = (u) => {
        // Navigate to Uploads with an ?utr= param — the Uploads page auto-opens the drawer
        const back = encodeURIComponent("/admin/ocr-recheck");
        const stamp = Date.now();
        navigate(`/admin/uploads?utr=${encodeURIComponent(u.utr || "")}&_open=${stamp}&status=all&back=${back}`);
    };

    return (
        <div className="space-y-6" data-testid="ocr-recheck-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Reconciliation</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">OCR Re-check</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Scan verified slips party-by-party. Flagged rows appear below and self-remove the moment you correct + save.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="ocr-flagged-refresh"
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                    <ArrowClockwise size={15} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <ReverifyPanel onSelectParty={(pid) => navigate(`/admin/uploads?party_id=${pid}&status=all`)} />

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="ocr-flagged-panel">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                    <div>
                        <div className="flex items-center gap-2 font-display text-sm font-semibold text-slate-900">
                            <Warning size={14} weight="fill" className="text-rose-600" /> Slips with OCR mismatches
                            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700" data-testid="ocr-flagged-count">
                                {rows.length}
                            </span>
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500">Only slips currently flagged are shown. Rows disappear the moment you save the correction.</div>
                    </div>
                    <div className="relative w-full max-w-xs">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            data-testid="ocr-flagged-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter by party or UTR…"
                            className="h-8 w-full rounded-md border border-slate-200 bg-white pl-7 pr-2 text-xs focus:border-blue-400 focus:outline-none"
                        />
                    </div>
                </div>
                {loading && rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-6">
                        <EmptyState
                            icon={Warning}
                            title={rows.length === 0 ? "All clean — no OCR mismatches" : `No rows match "${search}"`}
                            description={rows.length === 0 ? "Run a re-check to scan for mismatches." : "Try a different keyword."}
                        />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="ocr-flagged-table">
                            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left"><SortHeader field="party_name" sort={sort} onChange={setSort}>Party</SortHeader></th>
                                    <th className="px-3 py-2 text-left"><SortHeader field="utr" sort={sort} onChange={setSort}>UTR</SortHeader></th>
                                    <th className="px-3 py-2 text-right"><SortHeader field="amount" align="right" sort={sort} onChange={setSort}>Amount</SortHeader></th>
                                    <th className="px-3 py-2 text-left"><SortHeader field="payment_date" sort={sort} onChange={setSort}>Payment Date</SortHeader></th>
                                    <th className="px-3 py-2 text-left">Mismatch on</th>
                                    <th className="px-3 py-2 text-left">OCR suggested</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filtered.map((u) => {
                                    const fields = u.ocr_mismatch_fields || [];
                                    const sug = u.ocr_suggested || {};
                                    return (
                                        <tr key={u.id} data-testid={`ocr-flagged-row-${u.id}`} className="bg-rose-50/40 hover:bg-rose-50">
                                            <td className="px-3 py-2 font-medium text-slate-900">{u.party_name || "—"}</td>
                                            <td className="px-3 py-2 font-mono text-xs text-slate-700">{u.utr || "—"}</td>
                                            <td className="px-3 py-2 text-right font-mono">{fmt(u.amount)}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{u.payment_date || "—"}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex flex-wrap gap-1">
                                                    {fields.map((f) => (
                                                        <span key={f} className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700" data-testid={`ocr-flagged-field-${f}`}>
                                                            {f.replace(/_/g, " ")}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-600">
                                                <div className="space-y-0.5">
                                                    {fields.includes("utr") && <div><span className="text-slate-400">UTR:</span> <span className="font-mono text-rose-700">{sug.utr || "—"}</span></div>}
                                                    {fields.includes("amount") && <div><span className="text-slate-400">Amount:</span> <span className="font-mono text-rose-700">{sug.amount != null ? fmt(sug.amount) : "—"}</span></div>}
                                                    {fields.includes("payment_date") && <div><span className="text-slate-400">Date:</span> <span className="font-mono text-rose-700">{sug.payment_date || "—"}</span></div>}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    onClick={() => openSlip(u)}
                                                    data-testid={`ocr-flagged-open-${u.id}`}
                                                    className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
                                                >
                                                    <ArrowSquareOut size={12} weight="bold" /> Open slip
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
